import { cn, useTranslation } from '@hbs-us/ui';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { CHROME_BUTTON_CLASS, CHROME_BUTTON_RESTING_CLASS } from './Flyout.js';
import { SearchPanel } from './SearchPanel.js';

/**
 * The bar's search control: the trigger, the shortcut that opens it, and nothing
 * else. What the panel does with a query lives in `SearchPanel`.
 */
// The handler takes Meta OR Ctrl, so the hint has to say which one THIS keyboard
// uses — a Windows user told "⌘K" has been given a key they do not have. Read once
// at module scope: the platform cannot change mid-session.
const SHORTCUT_HINT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
    ? '⌘K'
    : 'Ctrl K';

export function CommandPalette() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      {/*
        Two shapes, one control. Below `lg` there is no room for a caption, so the
        trigger is simply another 44px chrome icon button — the same size, radius
        and hover as the launcher, the bell and the gear, instead of a shrunken
        field pretending to be an input. At `lg` it opens out into the field it
        really is, and the ⌘K cap parks on the far edge rather than trailing the
        caption across a half-empty box. Same square radius as every other control
        in the bar: nothing here is a lone pill.
      */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('nav:search.everything')}
        className={cn(
          CHROME_BUTTON_CLASS,
          CHROME_BUTTON_RESTING_CLASS,
          // At `lg` this is the bar's flexible middle: it takes the slack the tab
          // row used to hoard, up to a cap, so the field is generous without
          // becoming a 600px empty trough on a wide monitor.
          'lg:w-auto lg:min-w-[12rem] lg:max-w-[26rem] lg:flex-1 lg:justify-start lg:gap-2 ' +
            'lg:border lg:border-border lg:bg-background lg:px-3 lg:text-sm',
        )}
      >
        <Search className="size-4" aria-hidden="true" />
        <span className="hidden lg:inline">{t('nav:search.everything')}</span>
        <kbd className="hidden rounded border border-border bg-card px-1.5 py-0.5 font-mono text-2xs lg:ml-auto lg:inline">
          {SHORTCUT_HINT}
        </kbd>
      </button>
      <SearchPanel open={open} onOpenChange={setOpen} />
    </>
  );
}
